import {
  Risk_Assessment_Result_Control_Type_Enum,
  Risk_Scoring_Model_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRiskRatingResolver } from 'src/ratings/useRiskRatingResolver';

import { useAggregation } from '@/hooks/useAggregation';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { useRiskScore } from '@/hooks/useRiskScore';
import { toLocalDate } from '@/utils/dateUtils';

import { RiskScoreBreakdownPopover } from '../../risk-score-breakdown/RiskScoreBreakdownPopover';
import { SECTION_IDS } from '../constants';
import type {
  RatingSectionData,
  UseRiskOverviewPanelRatingsResult,
} from '../types';

export const useRiskOverviewPanelRatings = (
  riskId: string
): UseRiskOverviewPanelRatingsResult => {
  const { t } = useTranslation('common', { keyPrefix: 'riskOverviewPanel' });
  const score = useRiskScore(riskId);
  const { riskModel } = useAggregation();
  const { resolveRiskRating, resolveLikelihood, resolveImpact } =
    useRiskRatingResolver('standard', { configType: 'risk_assessment' });

  const {
    inherentRating,
    inherentLikelihood,
    inherentImpact,
    inherentCompletionDate,
    residualRating,
    residualLikelihood,
    residualImpact,
    residualCompletionDate,
    breakdownData,
  } = score;

  const isTypedModel =
    riskModel === Risk_Scoring_Model_Enum.TypedControlEffectivenessAverages;
  const isBreakdownModuleEnabled = useIsModuleEnabled(
    'risk.subModules.risk_score_breakdown'
  );
  const showBreakdownPopover =
    isBreakdownModuleEnabled && isTypedModel && breakdownData !== null;

  const sections = useMemo((): RatingSectionData[] => {
    const formatDate = (date: string | null | undefined) =>
      date ? toLocalDate(date) : undefined;

    const buildSection = (
      id: string,
      title: string,
      controlType: Risk_Assessment_Result_Control_Type_Enum,
      values: {
        rating: number | null | undefined;
        likelihood: number | null | undefined;
        impact: number | null | undefined;
        completionDate: string | null | undefined;
      },
      adornment?: ReactNode
    ): RatingSectionData => ({
      id,
      title,
      overallRating: resolveRiskRating({
        likelihood: values.likelihood ?? null,
        impact: values.impact ?? null,
        controlType,
        rating: values.rating,
      }),
      formattedDate: formatDate(values.completionDate),
      rows: [
        {
          label: t('ratings.likelihoodLabel'),
          rating: resolveLikelihood(values.likelihood),
          formattedDate: formatDate(values.completionDate),
        },
        {
          label: t('ratings.impactLabel'),
          rating: resolveImpact(values.impact),
          formattedDate: formatDate(values.completionDate),
        },
      ],
      adornment,
    });

    const residualAdornment =
      showBreakdownPopover && breakdownData ? (
        <RiskScoreBreakdownPopover
          breakdown={breakdownData}
          triggerLabel={t('ratings.breakdownPopoverTriggerLabel', {
            defaultValue: 'Why this score?',
          })}
        />
      ) : undefined;

    return [
      buildSection(
        SECTION_IDS.inherent,
        t('ratings.inherentTitle'),
        Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
        {
          rating: inherentRating,
          likelihood: inherentLikelihood,
          impact: inherentImpact,
          completionDate: inherentCompletionDate,
        }
      ),
      buildSection(
        SECTION_IDS.residual,
        t('ratings.residualTitle'),
        Risk_Assessment_Result_Control_Type_Enum.Controlled,
        {
          rating: residualRating,
          likelihood: residualLikelihood,
          impact: residualImpact,
          completionDate: residualCompletionDate,
        },
        residualAdornment
      ),
    ];
  }, [
    t,
    inherentRating,
    inherentLikelihood,
    inherentImpact,
    inherentCompletionDate,
    residualRating,
    residualLikelihood,
    residualImpact,
    residualCompletionDate,
    resolveRiskRating,
    resolveLikelihood,
    resolveImpact,
    showBreakdownPopover,
    breakdownData,
  ]);

  return {
    data: sections,
    loading: score.loading,
  };
};
